/* eslint-disable i18next/no-literal-string -- a prototype screen; its copy moves to the locales with the screen. */
import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "utils/cn";
import { SLEEK_CONTROL, SLEEK_PRIMARY } from "pages/DesignCanvasAgentPage/components/sleek";
import type { SleekSize } from "./SleekPicker";
import AgentWaitingText from "pages/DesignCanvasAgentPage/components/brand/AgentWaitingText";

/** How long an example stays (the composer's `EXAMPLE_HOLD_MS`, as `ConceptSearch`'s). */
const EXAMPLE_HOLD_MS = 3200;

/**
 * A plain search in `ConceptSearch`'s finish (Runway Trends' search): the same field, the page's examples arriving
 * letter by letter while it is empty (Tab writes the one shown in), an × and the search button. It searches as it is
 * typed in, after `wait` (the old search box's pause), or at once on Enter — the words themselves, no concept made.
 */
const SleekSearch = ({
  value,
  onSearch,
  examples = [],
  wait = 600,
  ariaLabel,
  placeholder,
  size = "md",
  className,
}: {
  value: string;
  onSearch: (text: string) => void;
  examples?: string[];
  wait?: number;
  ariaLabel: string;
  /** Said while it is empty and no examples run. */
  placeholder?: string;
  size?: SleekSize;
  className?: string;
}) => {
  const small = size === "sm";
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const send = (next: string, now = false) => {
    if (timer.current) clearTimeout(timer.current);
    if (now) onSearch(next.trim());
    else timer.current = setTimeout(() => onSearch(next.trim()), wait);
  };
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const [example, setExample] = useState(0);
  const cycling = !text && examples.length > 0;
  useEffect(() => {
    if (!cycling) return undefined;
    const id = window.setInterval(() => setExample((current) => (current + 1) % examples.length), EXAMPLE_HOLD_MS);

    return () => window.clearInterval(id);
  }, [cycling, examples.length]);
  const shown = examples[example % Math.max(examples.length, 1)] ?? "";

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        send(text, true);
      }}
      className={cn(SLEEK_CONTROL, "flex items-center gap-1.5 rounded-full", small ? "h-9 pl-3.5 pr-1" : "h-12 pl-5 pr-1.5", className)}
    >
      <span className="relative min-w-0 flex-1">
        <input
          value={text}
          maxLength={200}
          onChange={(event) => {
            setText(event.target.value);
            send(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Tab" && !event.shiftKey && !text && shown) {
              event.preventDefault();
              setText(shown);
              send(shown, true);
            }
          }}
          aria-label={ariaLabel}
          placeholder={cycling ? undefined : placeholder}
          className={cn("w-full bg-transparent text-trueBlack outline-none placeholder:text-grey dark:text-white", small ? "text-[13px]" : "text-[15px]")}
        />
        {cycling && (
          // The example, drawn over the empty box (a native placeholder cannot move).
          <span aria-hidden className={cn("pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap leading-none", small ? "text-[13px]" : "text-[15px]")}>
            <AgentWaitingText text={shown} tone="silver" weight="font-normal" />
          </span>
        )}
      </span>
      {text && (
        <button
          type="button"
          aria-label="Clear search"
          title="Clear search"
          onClick={() => {
            setText("");
            send("", true);
          }}
          className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-grey/15 text-grey transition-colors hover:bg-grey/25 hover:text-trueBlack dark:hover:text-white"
        >
          <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden>
            <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
          </svg>
        </button>
      )}
      <button
        type="submit"
        aria-label="Search"
        title="Search"
        className={cn(
          "flex flex-none items-center justify-center rounded-full transition-colors",
          small ? "h-7 w-7" : "h-9 w-9",
          text.trim() && text.trim() !== value ? SLEEK_PRIMARY : cn(SLEEK_CONTROL, "text-grey")
        )}
      >
        <Search size={small ? 14 : 17} />
      </button>
    </form>
  );
};

export default SleekSearch;
